import { useId } from 'react';
import { cx } from './cx';

export type GlyphTrancheStatus =
  | 'pending_conditions'
  | 'evidence_submitted'
  | 'cleared'
  | 'released'
  | 'withheld'
  | 'received'
  | 'cancelled';

export type GrantHealth = 'healthy' | 'attention' | 'risk';

export interface GrantHealthGlyphProps {
  /** ISO date the grant period opens. Omit and the period ring stays empty. */
  startAt?: string | null;
  endAt?: string | null;
  amountAwardedCents?: number | null;
  spentCents?: number | null;
  /** Only the status matters here; ticks sit in the order given. */
  tranches?: Array<{ status: GlyphTrancheStatus }>;
  /** Override the derived health when the page already knows better. */
  health?: GrantHealth;
  /** Rendered pixel size. Stays legible down to 20. */
  size?: number;
  /** Name of the grant, folded into the accessible label. */
  name?: string;
  /** Injectable for tests and for rendering a pack 'as at' a past date. */
  now?: Date;
  className?: string;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

function ratioOfPeriod(startAt?: string | null, endAt?: string | null, now = new Date()): number | null {
  if (!startAt || !endAt) return null;
  const start = Date.parse(startAt);
  const end = Date.parse(endAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return clamp01((now.getTime() - start) / (end - start));
}

/**
 * Health is derived rather than stored so the glyph never disagrees with the
 * figures beside it. The rule is the one a grants officer applies by hand:
 * money must track time. Spending far ahead of the calendar means the last
 * months are unfunded; spending far behind it near the end means an
 * underspend the funder will claw back. Both are worth a colour.
 */
export function deriveHealth(input: {
  elapsed: number | null;
  spent: number | null;
  tranches?: Array<{ status: GlyphTrancheStatus }>;
}): GrantHealth {
  const { elapsed, spent, tranches = [] } = input;
  if (tranches.some((t) => t.status === 'withheld')) return 'risk';
  if (spent !== null && spent > 1.02) return 'risk';
  if (elapsed !== null && spent !== null) {
    if (elapsed >= 0.95 && spent < 0.7) return 'risk';
    const drift = spent - elapsed;
    if (Math.abs(drift) > 0.2) return 'attention';
  }
  if (elapsed !== null && elapsed >= 0.85) return 'attention';
  return 'healthy';
}

/*
 * Drawn at 1.5px strokes with square caps so it sits with the Icon set and
 * with Instrument Sans. Tranche ticks vary in length and weight as well as colour,
 * and the health centre changes shape, not just fill. Board packs get printed in black and
 * white on an office laser, and a glyph that only works in colour becomes six
 * identical grey rings on the page that matters most.
 */
const trancheStyle: Record<GlyphTrancheStatus, { colour: string; length: number; width: number; label: string }> = {
  received: { colour: 'var(--ct-green)', length: 4.5, width: 2.5, label: 'received' },
  released: { colour: 'var(--ct-green)', length: 4.5, width: 2.5, label: 'approved for payment' },
  cleared: { colour: 'var(--ct-green)', length: 3.5, width: 1.5, label: 'cleared' },
  evidence_submitted: { colour: 'var(--ct-ink-3)', length: 3.5, width: 1.5, label: 'evidence submitted' },
  pending_conditions: { colour: 'var(--ct-ink-3)', length: 2.5, width: 1.5, label: 'pending' },
  withheld: { colour: 'var(--ct-red-700)', length: 4.5, width: 2.5, label: 'withheld' },
  cancelled: { colour: 'var(--ct-ink-3)', length: 2.5, width: 1.5, label: 'cancelled' },
};

const healthColour: Record<GrantHealth, string> = {
  healthy: 'var(--ct-green)',
  attention: 'var(--ct-amber-700)',
  risk: 'var(--ct-red-700)',
};

const healthWord: Record<GrantHealth, string> = {
  healthy: 'On track',
  attention: 'Needs attention',
  risk: 'At risk',
};

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * The signature mark. One 28px glyph carries a grant's whole state: the outer
 * ring is how much of the period has passed, the inner arc is how much of the
 * award has been spent, the ticks are the tranches, and the centre is health.
 * Reading the two arcs against each other is the entire job of grant
 * management, which is why they share one centre.
 */
export function GrantHealthGlyph({
  startAt,
  endAt,
  amountAwardedCents,
  spentCents,
  tranches = [],
  health,
  size = 28,
  name,
  now,
  className,
}: GrantHealthGlyphProps) {
  const titleId = useId();
  const elapsed = ratioOfPeriod(startAt, endAt, now ?? new Date());
  const awarded = amountAwardedCents ?? 0;
  const spent = awarded > 0 && spentCents != null ? spentCents / awarded : null;
  const state = health ?? deriveHealth({ elapsed, spent, tranches });

  // Geometry is fixed on a 32 box and scaled by the svg, so the stroke
  // relationships hold at 20px and at 64px alike.
  const C = 16;
  const rOuter = 13;
  const rInner = 9;
  const circOuter = 2 * Math.PI * rOuter;
  const circInner = 2 * Math.PI * rInner;
  const spentShown = spent === null ? 0 : clamp01(spent);
  const overspent = spent !== null && spent > 1;

  const label = [
    name ? `${name}:` : 'Grant status:',
    healthWord[state] + '.',
    elapsed === null ? 'Period not set.' : `${pct(elapsed)} of the grant period elapsed.`,
    spent === null
      ? 'Spend not tracked.'
      : `${pct(spent)} of the award spent${overspent ? ', over the awarded amount' : ''}.`,
    tranches.length
      ? `${tranches.length} tranche${tranches.length === 1 ? '' : 's'}: ${summariseTranches(tranches)}.`
      : 'No tranches.',
  ].join(' ');

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role="img"
      aria-labelledby={titleId}
      className={cx('shrink-0', className)}
    >
      <title id={titleId}>{label}</title>

      {/* Period track and elapsed arc. Rotated so both arcs start at twelve
          o'clock and read clockwise, the way a clock face does. */}
      <g transform="rotate(-90 16 16)">
        <circle cx={C} cy={C} r={rOuter} fill="none" stroke="var(--ct-chart-track)" strokeWidth="1.5" />
        {elapsed !== null ? (
          <circle
            cx={C}
            cy={C}
            r={rOuter}
            fill="none"
            stroke="var(--ct-ink-3)"
            strokeWidth="1.5"
            strokeLinecap="butt"
            strokeDasharray={`${(circOuter * elapsed).toFixed(2)} ${circOuter.toFixed(2)}`}
          />
        ) : null}

        {/* Spend. Heavier than the period ring so the two never blur into one
            another in greyscale, and dashed once it goes over the award. */}
        {spent !== null ? (
          <circle
            cx={C}
            cy={C}
            r={rInner}
            fill="none"
            stroke={overspent ? 'var(--ct-red-700)' : 'var(--ct-green)'}
            strokeWidth="3"
            strokeLinecap="butt"
            strokeDasharray={
              overspent
                ? '2.5 2'
                : `${(circInner * spentShown).toFixed(2)} ${circInner.toFixed(2)}`
            }
          />
        ) : null}
      </g>

      {/* Tranche ticks, spread evenly outside the period ring. */}
      {tranches.slice(0, 12).map((t, i, all) => {
        const style = trancheStyle[t.status] ?? trancheStyle.pending_conditions;
        const angle = (-90 + (360 / Math.max(all.length, 1)) * i) * (Math.PI / 180);
        const from = rOuter + 1.8;
        const to = from + style.length;
        return (
          <line
            key={i}
            x1={C + Math.cos(angle) * from}
            y1={C + Math.sin(angle) * from}
            x2={C + Math.cos(angle) * to}
            y2={C + Math.sin(angle) * to}
            stroke={style.colour}
            strokeWidth={style.width}
            strokeLinecap="square"
          />
        );
      })}

      {/* Health centre: a square marker. Shape carries the meaning as well
          as colour: filled for on track, hollow for attention, barred for
          at risk. */}
      {state === 'attention' ? (
        <rect x={C - 3} y={C - 3} width="6" height="6" fill="none" stroke={healthColour.attention} strokeWidth="1.5" />
      ) : (
        <rect x={C - 3.5} y={C - 3.5} width="7" height="7" fill={healthColour[state]} />
      )}
      {state === 'risk' ? (
        <line x1={C - 2.5} y1={C} x2={C + 2.5} y2={C} stroke="var(--ct-surface)" strokeWidth="1.5" strokeLinecap="square" />
      ) : null}
    </svg>
  );
}

function summariseTranches(tranches: Array<{ status: GlyphTrancheStatus }>): string {
  const counts = new Map<string, number>();
  for (const t of tranches) {
    const word = (trancheStyle[t.status] ?? trancheStyle.pending_conditions).label;
    counts.set(word, (counts.get(word) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([word, n]) => `${n} ${word}`)
    .join(', ');
}
